<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import HelpButton from '@/components/common/help/HelpButton.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import { useLeadStore } from '@/stores/useLeadStore'
import { useOrderStore } from '@/stores/useOrderStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useMyTeamStore } from '@/stores/useMyTeamStore'
import { formatDate } from '@/utils/dateUtils'
import LeadBadgeGrid from '@/components/data-visualization/grids/LeadBadgeGrid.vue'

const route = useRoute()
const router = useRouter()
const $toast = useToast()
const leadStore = useLeadStore()
const orderStore = useOrderStore()
const boostCoreStore = useBoostCorePermissionStore()
const myTeamStore = useMyTeamStore()

const leadUuid = computed(() => route.params.leadUuid as string)
const lead = ref<any | null>(null)
const isLoading = ref(false)
const isLoadingUpdate = ref(false)
const selectedUser = ref<string>('')
const statusChangeComment = ref<string>('')
const showAppointmentModal = ref(false)
const editingAppointmentDate = ref<string | null>(null)
const editingAppointmentTime = ref<string | null>(null)

const leadRecipients = [
  { label: 'ITAC', value: 'ITAC' },
  { label: 'UNIXO', value: 'UNIXO' },
  { label: 'GROUP SOLUTIONS', value: 'GROUP_SOLUTIONS' },
]

const isSuperAdmin = computed(() => boostCoreStore.include('SUPER_ADMIN'))
const canUpdateRecipient = computed(
  () => boostCoreStore.include('LEAD_ADMIN') || isSuperAdmin.value
)
const canEditFamilies = computed(
  () => boostCoreStore.include('LEAD_ADMIN') || isSuperAdmin.value
)

const selectedFamilies = ref<string[]>([])

const familyOptions = computed(() =>
  orderStore.ventilationFamilies.map((f) => ({
    id: f.code,
    value: f.code,
    label: f.label,
  }))
)

const leadFamilyLabels = computed(() => {
  const families = Array.isArray(lead.value?.families) ? lead.value.families : []
  return families.map((f: any) => f.label || f.code)
})
const canAssignLead = computed(
  () => lead.value?.source === 'h3a' || boostCoreStore.include('LEAD_ADMIN') || isSuperAdmin.value
)
const canModifyAppointment = computed(
  () => boostCoreStore.user?.uuid === lead.value?.ownerUuid || boostCoreStore.include('LEAD_ADMIN') || isSuperAdmin.value
)

const availableAuthors = computed(() => {
  if (isSuperAdmin.value) {
    return myTeamStore.all.map(member => ({
      id: member.uuid,
      value: member.uuid,
      label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email,
    }))
  }
  return myTeamStore.members.map(member => ({
    id: member.uuid,
    value: member.uuid,
    label: `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email,
  }))
})

const h3aMetadata = computed(() => {
  if (lead.value?.source !== 'h3a' || !lead.value?.metadata) {
    return null
  }

  const metadata = lead.value.metadata
  const offers = Array.isArray(metadata.offers) ? metadata.offers : []
  const street = metadata.street || ''
  const zipcode = metadata.zipcode || ''
  const city = metadata.city || ''
  const date = metadata.date || null
  const time = metadata.time || null

  if (metadata.environmentsInformatique) metadata.environmentsInformatique[0].name = 'Informatique'
  if (metadata.environmentsData) metadata.environmentsData[0].name = 'Data'
  if (metadata.environmentsFixe) metadata.environmentsFixe[0].name = 'Fixe'
  if (metadata.environmentsMobile) metadata.environmentsMobile[0].name = 'Mobile'
  if (metadata.environmentsIpbx) metadata.environmentsIpbx[0].name = 'IPBX'

  const environments = [
    ...Array.isArray(metadata.environmentsFixe) ? metadata.environmentsFixe : [],
    ...Array.isArray(metadata.environmentsMobile) ? metadata.environmentsMobile : [],
    ...Array.isArray(metadata.environmentsData) ? metadata.environmentsData : [],
    ...Array.isArray(metadata.environmentsIpbx) ? metadata.environmentsIpbx : [],
    ...Array.isArray(metadata.environmentsInformatique) ? metadata.environmentsInformatique : [],
  ]

  return {
    offers: offers.filter((offer: unknown) => offer !== null && offer !== undefined).map(String),
    address: [street, zipcode, city].filter(Boolean).join(' '),
    appointmentDate: date,
    appointmentTime: time,
    environments,
  }
})

const showCommentField = computed(() => {
  const status = lead.value?.status
  return status && status !== 'WON' && status !== 'LOST'
})

const showWaitingActions = computed(() => lead.value?.status === 'WAITING')
const showCloseActions = computed(
  () => lead.value?.status && lead.value?.status !== 'WAITING' && lead.value?.status !== 'WON' && lead.value?.status !== 'LOST'
)

function formatH3aOffers(offers: unknown[]) {
  if (!offers?.length) return 'Aucune offre renseignée'
  return offers.join(', ')
}

function getAuthorName() {
  if (lead.value?.author?.firstName || lead.value?.author?.lastName) {
    return `${lead.value.author.firstName || ''} ${lead.value.author.lastName || ''}`.trim()
  }
  return lead.value?.metadata?.authorName || 'Non défini'
}

function getOwnerName() {
  if (lead.value?.owner?.firstName || lead.value?.owner?.lastName) {
    return `${lead.value.owner.firstName || ''} ${lead.value.owner.lastName || ''}`.trim()
  }
  return 'Non attribué'
}

async function fetchLead() {
  if (!leadUuid.value) return
  isLoading.value = true
  try {
    const fetched = await leadStore.fetchLeadByUuid(leadUuid.value)
    lead.value = fetched
    selectedUser.value = fetched?.ownerUuid || ''
    selectedFamilies.value = Array.isArray(fetched?.families)
      ? fetched.families.map((f: any) => f.code)
      : []
  } catch (error) {
    $toast.error('Impossible de charger ce lead.')
  } finally {
    isLoading.value = false
  }
}

function goBack() {
  router.push('/dashboard/leads')
}

async function assignLead() {
  if (!selectedUser.value || !lead.value?.uuid) return
  isLoadingUpdate.value = true
  try {
    await leadStore.updateLead(lead.value.uuid, { ownerUuid: selectedUser.value })
    $toast.success('Lead attribué avec succès.')
    await fetchLead()
  } catch (error) {
    $toast.error('Échec de l\'attribution du lead. Veuillez réessayer.')
  } finally {
    isLoadingUpdate.value = false
  }
}

async function updateLeadRecipient(newRecipient: string) {
  if (!lead.value?.uuid) return
  isLoadingUpdate.value = true
  try {
    await leadStore.updateLead(lead.value.uuid, { recipient: newRecipient })
    $toast.success('Destinataire du lead mis à jour avec succès.')
    await fetchLead()
  } catch (error) {
    $toast.error('Échec de la mise à jour du destinataire du lead. Veuillez réessayer.')
  } finally {
    isLoadingUpdate.value = false
  }
}

async function updateLeadFamilies() {
  if (!lead.value?.uuid) return
  // Évite un PATCH inutile si la sélection n'a pas changé (le @close se déclenche à chaque fermeture)
  const current = Array.isArray(lead.value?.families)
    ? lead.value.families.map((f: any) => f.code)
    : []
  const next = selectedFamilies.value
  const unchanged = current.length === next.length && [...current].sort().join('|') === [...next].sort().join('|')
  if (unchanged) return
  isLoadingUpdate.value = true
  try {
    await leadStore.updateLead(lead.value.uuid, { familyCodes: selectedFamilies.value })
    $toast.success('Familles du lead mises à jour avec succès.')
    await fetchLead()
  } catch (error) {
    $toast.error('Échec de la mise à jour des familles. Veuillez réessayer.')
  } finally {
    isLoadingUpdate.value = false
  }
}

async function updateLeadStatus(newStatus: string) {
  if (!lead.value?.uuid) return
  isLoadingUpdate.value = true
  try {
    await leadStore.updateLead(lead.value.uuid, {
      status: newStatus,
      comment: statusChangeComment.value || undefined,
    })
    $toast.success(
      `Lead marqué comme ${
        newStatus === 'ACCEPTED'
          ? 'Accepté'
          : newStatus === 'REFUSED'
          ? 'Refusé'
          : newStatus === 'WON'
          ? 'Gagné'
          : 'Perdu'
      }.`,
    )
    statusChangeComment.value = ''
    await fetchLead()
  } catch (error) {
    $toast.error('Échec de la mise à jour du statut du lead. Veuillez réessayer.')
  } finally {
    isLoadingUpdate.value = false
  }
}

function openAppointmentModal() {
  if (h3aMetadata.value) {
    editingAppointmentDate.value = h3aMetadata.value.appointmentDate
    editingAppointmentTime.value = h3aMetadata.value.appointmentTime
    showAppointmentModal.value = true
  }
}

function closeAppointmentModal() {
  showAppointmentModal.value = false
  editingAppointmentDate.value = null
  editingAppointmentTime.value = null
}

async function updateAppointment() {
  if (!lead.value?.uuid || editingAppointmentDate.value === null || editingAppointmentTime.value === null) return
  isLoadingUpdate.value = true
  try {
    const updatedMetadata = { ...(lead.value.metadata || {}) }
    updatedMetadata.date = editingAppointmentDate.value
    updatedMetadata.time = editingAppointmentTime.value
    await leadStore.updateLead(lead.value.uuid, {
      data: updatedMetadata,
    })
    $toast.success('Rendez-vous reporté avec succès.')
    closeAppointmentModal()
    await fetchLead()
  } catch (error) {
    $toast.error('Échec de la mise à jour du rendez-vous. Veuillez réessayer.')
  } finally {
    isLoadingUpdate.value = false
  }
}

onMounted(async () => {
  await fetchLead()
  await orderStore.ensureVentilationFamilies()
})
watch(leadUuid, fetchLead)
</script>

<template>
  <div class="min-h-screen bg-base-200/40">
    <div class="px-4 py-6 max-w-[1200px] mx-auto w-full">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div class="flex items-center gap-1">
            <h1 class="text-2xl font-semibold">Détail du lead</h1>
            <HelpButton />
          </div>
          <p class="text-sm text-base-content/70">Consultation et gestion complète du lead sélectionné</p>
        </div>
        <div class="flex flex-wrap gap-2">
          <ButtonDaisy text="Retour" variant="ghost" @click="goBack" />
        </div>
      </div>

      <div v-if="isLoading" class="mt-6 card bg-base-100 shadow">
        <div class="card-body">
          <span class="loading loading-spinner loading-lg"></span>
        </div>
      </div>

      <div v-else-if="lead" class="mt-6 space-y-6">
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div class="card bg-base-100 shadow">
            <div class="card-body space-y-4">
              <div class="flex items-center justify-between gap-4">
                <div>
                  <h2 class="card-title text-lg">Informations</h2>
                  <p class="text-sm text-base-content/70">Données principales du lead</p>
                </div>
                <LeadBadgeGrid :params="{ data: {status: lead.status}, fitWidth: true}"/>

              </div>

              <div class="space-y-3 text-sm text-base-content/80">
                <div>
                  <span class="font-semibold">Type :</span>
                  <span>{{ lead.type || '—' }}</span>
                </div>
                <div>
                  <span class="font-semibold">Source :</span>
                  <span>{{ lead.source || '—' }}</span>
                </div>
                <div>
                  <span class="font-semibold">Destinataire :</span>
                  <span v-if="!canUpdateRecipient">{{ lead.recipient || '—' }}</span>
                  <select
                    v-else
                    v-model="lead.recipient"
                    @change="updateLeadRecipient(lead.recipient)"
                    class="select select-bordered w-full"
                  >
                    <option v-for="recipient in leadRecipients" :key="recipient.value" :value="recipient.value">
                      {{ recipient.label }}
                    </option>
                  </select>
                </div>
                <div>
                  <span class="font-semibold">Auteur :</span>
                  <span>{{ getAuthorName() }}</span>
                </div>
                <div>
                  <span class="font-semibold">Attribué à :</span>
                  <span>{{ getOwnerName() }}</span>
                </div>
                <div>
                  <span class="font-semibold">Créé le :</span>
                  <span>{{ formatDate(lead.createdAt?.toString() || '') }}</span>
                </div>
                <div>
                  <span class="font-semibold">Familles :</span>
                  <template v-if="!canEditFamilies">
                    <span v-if="leadFamilyLabels.length" class="flex flex-wrap gap-1 mt-1">
                      <span v-for="label in leadFamilyLabels" :key="label" class="badge badge-primary badge-sm">{{ label }}</span>
                    </span>
                    <span v-else>—</span>
                  </template>
                  <SelectInput
                    v-else
                    v-model="selectedFamilies"
                    label=""
                    :options="familyOptions"
                    :multiple="true"
                    placeholder="Sélectionner une ou plusieurs familles"
                    @close="updateLeadFamilies"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="card bg-base-100 shadow xl:col-span-2">
            <div class="card-body space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div class="text-sm opacity-70 mb-2">Contact</div>
                  <div class="space-y-2 text-sm">
                    <div><span class="font-semibold">Prénom :</span> {{ lead.contactFirstname || '—' }}</div>
                    <div><span class="font-semibold">Nom :</span> {{ lead.contactLastname || '—' }}</div>
                    <div><span class="font-semibold">Email :</span> {{ lead.contactEmail || '—' }}</div>
                    <div><span class="font-semibold">Téléphone :</span> {{ lead.contactPhone || '—' }}</div>
                  </div>
                </div>
                <div>
                  <div class="text-sm opacity-70 mb-2">Société</div>
                  <div class="space-y-2 text-sm">
                    <div><span class="font-semibold">Nom :</span> {{ lead.companyName || '—' }}</div>
                    <div><span class="font-semibold">SIRET :</span> {{ lead.companySiret || '—' }}</div>
                  </div>
                </div>
              </div>

              <div>
                <div class="text-sm opacity-70 mb-2">Notes</div>
                <div class="p-4 rounded-lg bg-base-200/60 text-sm whitespace-pre-wrap">
                  {{ lead.metadata?.notes || lead.metadata?.comments?.[0]?.text || 'Aucune note renseignée' }}
                </div>
              </div>

              <div v-if="canAssignLead && !lead.ownerUuid" class="rounded-lg border border-base-200 bg-base-100 p-4 space-y-4">
                <div class="font-semibold">Attribution du lead</div>
                <SelectInput
                  v-model="selectedUser"
                  label="Attribuer à"
                  :options="[{ id: '', label: 'Tous', value: '' }, ...availableAuthors]"
                  placeholder="Sélectionner un commercial"
                  class="max-w-[320px]"
                />
                <div class="flex justify-end">
                  <ButtonDaisy
                    text="Attribuer"
                    variant="primary"
                    :disabled="!selectedUser || isLoadingUpdate"
                    @click="assignLead"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="h3aMetadata" class="space-y-6">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="rounded-lg border border-base-200 bg-base-100 p-4">
              <div class="flex items-center justify-between gap-4 mb-2">
                <div class="text-sm font-medium text-base-content/70">Rendez-vous</div>
                <button
                  v-if="canModifyAppointment"
                  @click="openAppointmentModal"
                  class="btn btn-xs btn-outline"
                  title="Reporter le rendez-vous"
                >
                  Reporter
                </button>
              </div>
              <div class="text-sm space-y-2">
                <div><span class="font-semibold">Date :</span> {{ new Date(h3aMetadata.appointmentDate).toLocaleDateString() || '—' }}</div>
                <div><span class="font-semibold">Heure :</span> {{ h3aMetadata.appointmentTime || '—' }}</div>
              </div>
              <div class="text-sm font-medium text-base-content/70 mb-2 mt-4">Adresse</div>
              <div class="text-sm">{{ h3aMetadata.address || 'Aucune adresse renseignée' }}</div>
            </div>
            <div class="rounded-lg border border-base-200 bg-base-100 p-4">
              <div class="text-sm font-medium text-base-content/70 mb-2">Offres</div>
              <div class="text-sm">{{ formatH3aOffers(h3aMetadata.offers) }}</div>
            </div>
          </div>

          <div v-if="h3aMetadata.environments.length" class="space-y-4">
            <div class="text-sm font-medium text-base-content/70 mb-2">Environnements</div>
            <div class="grid gap-4 md:grid-cols-2">
              <div
                v-for="(environment, index) in h3aMetadata.environments"
                :key="`env-${index}`"
                class="rounded-lg border border-base-200 bg-base-100 p-4"
              >
                <div class="space-y-3 text-sm">
                  <div class="font-semibold">{{ environment.name || `Environnement ${index + 1}` }}</div>
                  <div v-if="environment.operator">Opérateur : {{ environment.operator ?? '—' }}</div>
                  <div v-if="environment.post">Nombre Poste : {{ environment.post ?? '—' }}</div>
                  <div v-if="environment.communication">Nombre Communication : {{ environment.communication ?? '—' }}</div>
                  <div v-if="environment.line !== undefined">Nombre Ligne : {{ environment.line ?? '—' }}</div>
                  <div v-if="environment.typeAccess !== undefined">Type d'accès : {{ environment.typeAccess ?? '—' }}</div>
                  <div v-if="environment.type !== undefined">Type : {{ environment.type ?? '—' }}</div>
                  <div v-if="environment.nbUsers !== undefined">Nombre d'utilisateurs : {{ environment.nbUsers ?? '—' }}</div>
                  <div v-if="environment.nbSites !== undefined">Nombre de sites : {{ environment.nbSites ?? '—' }}</div>
                  <div v-if="environment.server !== undefined">Serveur : {{ environment.server ?? '—' }}</div>
                  <div v-if="environment.serverAge !== undefined">Âge du serveur : {{ environment.serverAge ?? '—' }}</div>
                  <div v-if="environment.pc !== undefined">Nombre d'ordinateurs : {{ environment.pc ?? '—' }}</div>
                  <div v-if="environment.pcAge !== undefined">Âge des ordinateurs : {{ environment.pcAge ?? '—' }}</div>
                  <div v-if="environment.firewall !== undefined">Firewall : {{ environment.firewall ? 'Oui' : 'Non' }}</div>
                  <div v-if="environment.firewallAge !== undefined">Âge du firewall : {{ environment.firewallAge ?? '—' }}</div>
                  <div v-if="environment.antiSpam !== undefined">AntiSpam : {{ environment.antiSpam ? 'Oui' : 'Non' }}</div>
                  <div v-if="environment.antiSpamAge !== undefined">Âge de l'antiSpam : {{ environment.antiSpamAge ?? '—' }}</div>
                  <div v-if="environment.antiVirus !== undefined">Antivirus : {{ environment.antiVirus ? 'Oui' : 'Non' }}</div>
                  <div v-if="environment.antiVirusAge !== undefined">Âge de l'antivirus : {{ environment.antiVirusAge ?? '—' }}</div>
                  <div v-if="environment.outSourcingDfeAt !== undefined || environment.dfeAt !== undefined">
                    Date de fin d'engagement : {{ typeof environment.outSourcingDfeAt === 'string' ? new Date(environment.outSourcingDfeAt).toLocaleDateString() : typeof environment.dfeAt === 'string' ? new Date(environment.dfeAt).toLocaleDateString() : '—' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showCommentField" class="space-y-4">
          <div>
            <label class="label mb-2">
              <span class="label-text">Commentaire (optionnel)</span>
            </label>
            <textarea
              v-model="statusChangeComment"
              placeholder="Ajouter un commentaire pour cette action..."
              class="textarea textarea-bordered w-full"
              rows="3"
            ></textarea>
          </div>
          <div v-if="showWaitingActions || showCloseActions" class="flex flex-wrap gap-2 justify-end">
            <ButtonDaisy v-if="showWaitingActions" text="Accepter" variant="success" :disabled="isLoadingUpdate" @click="updateLeadStatus('ACCEPTED')" />
            <ButtonDaisy v-if="showWaitingActions" text="Refuser" variant="error" :disabled="isLoadingUpdate" @click="updateLeadStatus('REFUSED')" />
            <ButtonDaisy v-if="showCloseActions" text="Gagné" variant="success" :disabled="isLoadingUpdate" @click="updateLeadStatus('WON')" />
            <ButtonDaisy v-if="showCloseActions" text="Perdu" variant="error" :disabled="isLoadingUpdate" @click="updateLeadStatus('LOST')" />
          </div>
        </div>

        <div v-if="lead.leadComments?.length" class="space-y-4">
          <div class="font-semibold">Commentaires</div>
          <div class="space-y-3 rounded-lg border border-base-300 bg-base-100 p-4">
            <div v-for="comment in lead.leadComments" :key="comment.uuid" class="rounded-md border border-base-200 bg-base-200 p-3">
              <div class="flex items-center justify-between gap-4 text-sm text-base-content/70">
                <span>{{ comment.author?.firstname || 'Utilisateur inconnu' }} {{ comment.author?.lastname || '' }}</span>
                <span>{{ new Date(comment.createdAt).toLocaleString('fr-FR') }}</span>
              </div>
              <div class="mt-2 text-sm text-base-content">{{ comment.comment }}</div>
            </div>
          </div>
        </div>

        <!-- Modal Reporter Rendez-vous -->
        <div v-if="showAppointmentModal" class="modal modal-open">
          <div class="modal-box w-11/12 max-w-md">
            <h3 class="font-bold text-lg">Reporter le rendez-vous</h3>
            <div class="py-4 space-y-4">
              <div>
                <label class="label mb-2">
                  <span class="label-text">Date</span>
                </label>
                <input
                  v-model="editingAppointmentDate"
                  type="date"
                  class="input input-bordered w-full"
                />
              </div>
              <div>
                <label class="label mb-2">
                  <span class="label-text">Heure</span>
                </label>
                <input
                  v-model="editingAppointmentTime"
                  type="time"
                  class="input input-bordered w-full"
                />
              </div>
            </div>
            <div class="modal-action">
              <button @click="closeAppointmentModal" class="btn btn-ghost">Annuler</button>
              <button
                @click="updateAppointment"
                :disabled="isLoadingUpdate"
                class="btn btn-primary"
              >
                {{ isLoadingUpdate ? 'Mise à jour...' : 'Valider' }}
              </button>
            </div>
          </div>
          <form method="dialog" class="modal-backdrop" @submit="closeAppointmentModal">
            <button>close</button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
